import React from 'react';
import LockIcon from '@mui/icons-material/Lock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import styles from './FeatureLockBanner.module.css';
import { BUSINESS_PLAN_NAME, LIMIT_UPGRADE_BUTTON, LIMIT_UPGRADE_HINT, planAvailabilityText, planLimitText } from './planLockTexts';
import useCurrentPlanLabel from './useCurrentPlanLabel';

/**
 * Banner inline exibido quando um limite de plano é atingido ou uma
 * permissão não está disponível no plano atual.
 *
 * Princípio: NUNCA ocultar features — apenas avisar e oferecer upgrade.
 *
 * @param {Object} props
 * @param {'limit'|'permission'} props.type - Tipo do aviso
 * @param {string} props.featureName - Nome amigável da feature. Em type='limit',
 *   o item no plural e minúsculo (ex: "salas").
 * @param {string} [props.featureNameSingular] - Item no singular (type='limit'),
 *   usado quando o limite é 1 ("1 sala").
 * @param {number} [props.currentCount] - Quantidade atual (apenas para type='limit')
 * @param {number} [props.limit] - Limite do plano (apenas para type='limit')
 * @param {Function} props.onUpgradeClick - Callback ao clicar em "Fazer upgrade"
 * @param {string} [props.hint] - O que fazer no limite (type='limit'), de
 *   limitActionHint(); sem ele, "Mude de plano para adicionar mais."
 * @param {string} [props.planName] - Plano que libera a feature (type='permission').
 *   Quando informado, a mensagem diz "disponível a partir do plano X" (ou
 *   "no plano Business") em vez do genérico "não está disponível no seu plano".
 * @param {boolean} [props.plural] - featureName no plural ("estão disponíveis").
 * @param {boolean} [props.lockIcon] - Em type='limit', usa o cadeado em vez do
 *   alerta, como os avisos de plano (Salas, Procedimentos).
 * @param {string} [props.className] - Classe CSS adicional opcional
 */
const FeatureLockBanner = ({
  type,
  featureName,
  featureNameSingular,
  currentCount,
  limit,
  onUpgradeClick,
  planName,
  plural = false,
  lockIcon = false,
  hint = LIMIT_UPGRADE_HINT,
  className = ''
}) => {
  const planLabel = useCurrentPlanLabel();

  const getMessage = () => {
    if (type === 'limit') {
      return `${planLimitText(planLabel, limit, featureNameSingular || featureName, featureName)}. ${hint}`;
    }
    if (planName) {
      return (
        <>
          <strong>{featureName}</strong> {planAvailabilityText(planName, plural)}.
          Mude de plano para acessar.
        </>
      );
    }
    return (
      <>
        <strong>{featureName}</strong> não está disponível no seu plano atual.
        Faça upgrade para acessar.
      </>
    );
  };

  const Icon = type === 'limit' && !lockIcon ? WarningAmberIcon : LockIcon;

  return (
    <div className={`${styles.banner} ${className}`} role="alert">
      <div className={styles.content}>
        <Icon className={styles.icon} fontSize="small" />
        <span className={styles.message}>{getMessage()}</span>
      </div>
      <button
        className={styles.upgradeButton}
        onClick={onUpgradeClick}
        type="button"
      >
        {planName === BUSINESS_PLAN_NAME ? 'Falar com a gente' : LIMIT_UPGRADE_BUTTON}
      </button>
    </div>
  );
};

export default FeatureLockBanner;
